import { Link } from 'wouter';
import { asset } from '@/lib/basePath';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-200">
        <div className="container-custom py-4">
          <Link href="/">
            <a className="flex items-center">
              <img 
                src={asset('/signaconnect-logo.png')} 
                alt="SignaConnect Logo" 
                className="h-10 w-auto"
              />
            </a>
          </Link>
        </div>
      </header>

      <main className="container-custom py-12 max-w-4xl">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">SignaConnect Terms &amp; Conditions</h1>
        <p className="text-gray-500 text-sm mb-1">Effective Date: June 4, 2026</p>
        <p className="text-gray-400 text-xs mb-2 font-mono" data-terms-version="SC-TOS-2026-06-04">Terms Version: SC-TOS-2026-06-04</p>
        <p className="text-gray-700 mb-10 leading-relaxed">
          These SignaConnect Terms &amp; Conditions ("Terms") govern the use, deployment, installation, operation, and
          maintenance of SignaConnect solutions and services provided by SignaConnect LLC ("SignaConnect") and accepted
          by a property owner, operator, manager, tenant, licensee, or authorized representative ("Customer") through
          an executed proposal, statement of work, order form, electronic authorization, or other written acceptance.
        </p>
        <p className="text-gray-700 mb-10 leading-relaxed font-medium">
          By executing a proposal or authorizing deployment of any SignaConnect solution, Customer agrees to be bound
          by these Terms.
        </p>

        <div className="prose prose-lg max-w-none">

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Services</h2>
            <p className="text-gray-700 mb-4">
              SignaConnect provides connectivity enhancement, wireless infrastructure, carrier offload, data collection,
              analytics, EV charging, and related technology solutions (collectively, the "Services").
            </p>
            <p className="text-gray-700 mb-4">Services may include:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Carrier enhancement solutions</li>
              <li>Wireless access point deployments</li>
              <li>Carrier offload services</li>
              <li>Passpoint and Hotspot 2.0 integrations</li>
              <li>Data receiver deployments</li>
              <li>Analytics and reporting services</li>
              <li>EV charging infrastructure</li>
              <li>Future technology products offered by SignaConnect</li>
            </ul>
            <p className="text-gray-700">
              The specific Services authorized by Customer shall be identified within the applicable proposal.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Site Access</h2>
            <p className="text-gray-700 mb-4">
              Customer grants SignaConnect and its authorized contractors, partners, carriers, and service providers
              reasonable access to the property identified in the applicable proposal for purposes of site review,
              design, installation, testing, maintenance, repair, replacement, and removal.
            </p>
            <p className="text-gray-700 mb-4">
              Except in emergencies, SignaConnect shall provide reasonable advance notice before accessing restricted areas.
            </p>
            <p className="text-gray-700">
              Customer shall cooperate in providing reasonable access required for deployment and operation of the Services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Customer Responsibilities</h2>
            <p className="text-gray-700 mb-4">Customer shall:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Provide reasonable access to the property</li>
              <li>Designate an authorized point of contact</li>
              <li>Cooperate with reasonable technical reviews</li>
              <li>Coordinate access with applicable vendors or contractors when required</li>
              <li>Provide accurate information concerning existing infrastructure when available</li>
            </ul>
            <p className="text-gray-700">
              Customer shall not intentionally interfere with the operation of SignaConnect equipment or Services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. SignaConnect Equipment</h2>
            <p className="text-gray-700 mb-4">
              Unless otherwise agreed in writing, all equipment deployed by SignaConnect remains the sole property of
              SignaConnect. Customer acquires no ownership interest in SignaConnect equipment.
            </p>
            <p className="text-gray-700 mb-4">
              SignaConnect retains sole authority to maintain, modify, replace, relocate, upgrade, or remove its equipment.
            </p>
            <p className="text-gray-700">
              Upon expiration or termination of the applicable agreement, SignaConnect may remove its equipment within
              a commercially reasonable period.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. No Guarantee of Performance</h2>
            <p className="text-gray-700 mb-4">
              SignaConnect will use commercially reasonable efforts to provide and maintain the Services.
            </p>
            <p className="text-gray-700 mb-4">
              Customer acknowledges that wireless communications, carrier participation, network availability, internet
              connectivity, and third-party systems are outside SignaConnect's control.
            </p>
            <p className="text-gray-700">
              SignaConnect does not guarantee specific coverage levels, signal strength, bandwidth availability, carrier
              participation, network uptime, revenue generation, data volumes, or financial performance.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Third-Party Networks and Carriers</h2>
            <p className="text-gray-700 mb-4">
              The Services may rely upon cellular carriers, internet service providers, data providers, cloud providers,
              utility providers, and other third-party technology vendors.
            </p>
            <p className="text-gray-700">
              SignaConnect is not responsible for service interruptions, delays, changes, or failures caused by third parties.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Confidentiality</h2>
            <p className="text-gray-700 mb-4">
              Each party may receive confidential information relating to the other party's business, operations,
              technology, finances, customers, or services.
            </p>
            <p className="text-gray-700 mb-4">Each party agrees to:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Protect confidential information using reasonable care</li>
              <li>Use confidential information solely for purposes of the relationship</li>
              <li>Not disclose confidential information except as required by law</li>
            </ul>
            <p className="text-gray-700">
              These obligations survive termination for five (5) years. Trade secrets remain protected for so long as
              they qualify as trade secrets under applicable law.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Intellectual Property</h2>
            <p className="text-gray-700 mb-4">
              All intellectual property rights associated with the Services, software, hardware, systems, analytics,
              documentation, processes, and technology remain the exclusive property of SignaConnect or its licensors.
            </p>
            <p className="text-gray-700">
              Customer shall not reverse engineer, decompile, disassemble, copy, modify, replicate, or create derivative
              works of any SignaConnect technology without prior written consent.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Data Rights</h2>
            <p className="text-gray-700 mb-4">
              SignaConnect may collect, process, analyze, aggregate, and utilize operational, network, performance, and
              site-level information generated through the Services.
            </p>
            <p className="text-gray-700 mb-4">
              Unless expressly stated otherwise in a written agreement, SignaConnect retains ownership of all analytics,
              methodologies, models, and aggregated data.
            </p>
            <p className="text-gray-700 mb-4">
              SignaConnect shall not knowingly sell personally identifiable information collected directly from Customer
              through the Services.
            </p>
            <p className="text-gray-700">
              Any data-sharing arrangements shall be governed by applicable law and any executed proposal.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Compliance with Law</h2>
            <p className="text-gray-700">
              Each party shall comply with all applicable federal, state, and local laws and regulations relating to
              its obligations under the Services.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Insurance</h2>
            <p className="text-gray-700 mb-4">
              Each party shall maintain commercially reasonable insurance coverage appropriate to its operations and
              responsibilities.
            </p>
            <p className="text-gray-700">
              Evidence of insurance may be provided upon reasonable request.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Indemnification</h2>
            <p className="text-gray-700 mb-4">
              Each party agrees to indemnify, defend, and hold harmless the other party from third-party claims,
              damages, losses, liabilities, and expenses arising from its negligence, willful misconduct, or breach
              of these Terms.
            </p>
            <p className="text-gray-700">
              The indemnified party shall promptly notify the indemnifying party of any claim.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Limitation of Liability</h2>
            <p className="text-gray-700">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY SHALL BE LIABLE FOR LOST PROFITS, LOST REVENUE,
              LOST BUSINESS OPPORTUNITIES, LOSS OF GOODWILL, INDIRECT DAMAGES, INCIDENTAL DAMAGES, SPECIAL DAMAGES,
              PUNITIVE DAMAGES, OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR RELATED TO THE SERVICES.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Force Majeure</h2>
            <p className="text-gray-700 mb-4">
              Neither party shall be liable for delays or failures caused by events beyond its reasonable control,
              including natural disasters, acts of God, government actions, war, terrorism, labor disruptions, utility
              failures, internet outages, carrier outages, pandemics, or supply chain disruptions.
            </p>
            <p className="text-gray-700">
              Performance obligations shall be suspended during the applicable force majeure event.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Term and Termination</h2>
            <p className="text-gray-700 mb-4">
              The commercial term applicable to a Customer relationship shall be set forth in the executed proposal.
            </p>
            <p className="text-gray-700 mb-4">
              Either party may terminate for material breach if such breach remains uncured for thirty (30) days
              following written notice.
            </p>
            <p className="text-gray-700 mb-4">Upon termination:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-1">
              <li>Revenue share obligations accrued prior to termination remain payable</li>
              <li>Confidentiality obligations survive</li>
              <li>Intellectual property protections survive</li>
              <li>SignaConnect may remove its equipment</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Assignment</h2>
            <p className="text-gray-700 mb-4">
              Either party may assign its rights and obligations to an affiliate, successor entity, or purchaser of
              substantially all assets without consent.
            </p>
            <p className="text-gray-700">
              Any other assignment requires reasonable prior written consent.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Electronic Signatures</h2>
            <p className="text-gray-700 mb-4">
              Electronic signatures, electronic records, electronic acknowledgments, and electronic acceptance methods
              shall be deemed legally valid and enforceable.
            </p>
            <p className="text-gray-700">
              Customer agrees that execution through SignaConnect's online proposal and authorization platform
              constitutes a valid signature.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Governing Law</h2>
            <p className="text-gray-700 mb-4">
              These Terms shall be governed by the laws of the Commonwealth of Pennsylvania without regard to
              conflict-of-law principles.
            </p>
            <p className="text-gray-700">
              Any dispute shall be resolved exclusively in the state or federal courts located in Philadelphia County,
              Pennsylvania.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Entire Agreement</h2>
            <p className="text-gray-700 mb-4">
              These Terms, together with the applicable proposal, constitute the entire agreement between the parties
              regarding the Services.
            </p>
            <p className="text-gray-700">
              In the event of a conflict between a proposal and these Terms, the proposal shall control solely with
              respect to the specific commercial terms contained within that proposal.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">20. Modifications</h2>
            <p className="text-gray-700 mb-4">
              SignaConnect may update these Terms from time to time.
            </p>
            <p className="text-gray-700">
              The version in effect on the date a proposal is executed shall govern that proposal unless otherwise
              agreed in writing.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">21. Contact Information</h2>
            <p className="text-gray-700 mb-4">
              For questions regarding these Terms, please contact:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-2"><strong>SignaConnect LLC</strong></p>
              <p className="text-gray-700 mb-2">
                Email:{' '}
                <a href="mailto:legal@signaconnect.com" className="text-primary-600 hover:text-primary-700">
                  legal@signaconnect.com
                </a>
              </p>
              <p className="text-gray-700">
                Website:{' '}
                <a href="https://signaconnect.com" className="text-primary-600 hover:text-primary-700">
                  https://signaconnect.com
                </a>
              </p>
            </div>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-8">
            <p className="text-gray-600 text-sm leading-relaxed">
              <strong>Note on Proposal-Specific Terms:</strong> Where a Customer has executed a proposal with
              SignaConnect, the terms of that proposal control with respect to the specific commercial arrangements
              set out therein, including pricing, service scope, revenue share, and term length. In the event of any
              conflict between these Terms and an executed proposal, the proposal shall govern for those specific
              commercial matters.
            </p>
          </div>

        </div>
      </main>

      <footer className="border-t border-gray-200 mt-16">
        <div className="container-custom py-8">
          <p className="text-center text-gray-600 text-sm">
            © 2025 SignaConnect. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
